import React, { useRef, useState } from "react";
import {
  Box,
  TextField,
  RadioGroup,
  FormControlLabel,
  Radio,
  Button,
  Menu,
  MenuItem,
  Typography,
} from "@material-ui/core";
import { ToggleButton, ToggleButtonGroup } from "@material-ui/lab";
import EditableField from "../../../../EditableField/EditableField";
import { LOGO_SENTINEL } from "./gridLogic";

// Variables que siempre están disponibles para insertar en el texto de una
// celda, sin importar qué se haya seleccionado en la Fase 2 — mismo patrón
// de "[variable]" que ya usa el editor de bloques de correo.
const VARIABLES_SIEMPRE_DISPONIBLES = [
  { key: "nombreCompleto", label: "Nombre completo" },
  { key: "numero", label: "Número de dorsal" },
  { key: "modalidad", label: "Modalidad" },
  { key: "rama", label: "Género" },
  { key: "categoria", label: "Categoría" },
];

export default function CellPropertyPanel({
  cell,
  selectedAttributes,
  atributosDisponibles,
  eventoIndex,
  onChange,
}) {
  const textFieldRef = useRef(null);
  const [anchorVariables, setAnchorVariables] = useState(null);

  const atributosPorKey = {};
  (atributosDisponibles || []).forEach((a) => {
    atributosPorKey[a.key] = a;
  });
  const variablesDisponibles = [
    ...VARIABLES_SIEMPRE_DISPONIBLES,
    ...(selectedAttributes || [])
      .filter((key) => !["modalidad", "rama", "categoria"].includes(key))
      .map((key) => ({
        key,
        label: (atributosPorKey[key] && atributosPorKey[key].label) || key,
      })),
  ];

  const tipo = cell.content?.type || "text";

  function cambiarTipo(nuevoTipo) {
    if (nuevoTipo === "text") {
      onChange({ content: { type: "text", value: "" } });
    } else if (nuevoTipo === "image") {
      onChange({ content: { type: "image", src: LOGO_SENTINEL } });
    }
  }

  function insertarVariable(key) {
    const token = `[${key}]`;
    const input = textFieldRef.current;
    const valorActual = cell.content.value || "";
    if (input && typeof input.selectionStart === "number") {
      const inicio = input.selectionStart;
      const fin = input.selectionEnd;
      const nuevoValor = valorActual.slice(0, inicio) + token + valorActual.slice(fin);
      onChange({ content: { type: "text", value: nuevoValor } });
    } else {
      onChange({ content: { type: "text", value: valorActual + token } });
    }
    setAnchorVariables(null);
  }

  return (
    <Box>
      <Box display="flex" style={{ gap: 12 }} mb={2}>
        <TextField
          label="Ancho (1-12)"
          type="number"
          size="small"
          value={cell.width}
          inputProps={{ min: 1, max: 12 }}
          onChange={(e) => onChange({ width: Number(e.target.value) })}
          style={{ width: 140 }}
        />
        {tipo === "text" && (
          <TextField
            label="Tamaño de fuente"
            type="number"
            size="small"
            value={cell.fontSize}
            inputProps={{ min: 4, max: 40 }}
            onChange={(e) => onChange({ fontSize: Number(e.target.value) })}
            style={{ width: 140 }}
          />
        )}
        <ToggleButtonGroup
          value={cell.orientation || "horizontal"}
          exclusive
          onChange={(e, val) => val && onChange({ orientation: val })}
          size="small"
        >
          <ToggleButton value="horizontal">Horizontal</ToggleButton>
          <ToggleButton value="vertical">Vertical</ToggleButton>
        </ToggleButtonGroup>
      </Box>

      <RadioGroup row value={tipo} onChange={(e) => cambiarTipo(e.target.value)}>
        <FormControlLabel value="text" control={<Radio />} label="Texto" />
        <FormControlLabel value="image" control={<Radio />} label="Imagen" />
      </RadioGroup>

      {tipo === "text" && (
        <Box mb={1}>
          <TextField
            fullWidth
            multiline
            rows={2}
            inputRef={textFieldRef}
            label="Texto de la celda"
            value={cell.content.value || ""}
            onChange={(e) => onChange({ content: { type: "text", value: e.target.value } })}
            helperText="Puedes combinar texto fijo con datos del atleta entre corchetes, por ejm [modalidad] - [categoria]"
          />
          <Button size="small" onClick={(e) => setAnchorVariables(e.currentTarget)} style={{ marginTop: 4 }}>
            Insertar variable
          </Button>
          <Menu anchorEl={anchorVariables} open={!!anchorVariables} onClose={() => setAnchorVariables(null)}>
            {variablesDisponibles.map((v) => (
              <MenuItem key={v.key} onClick={() => insertarVariable(v.key)}>
                {v.label}
              </MenuItem>
            ))}
          </Menu>
        </Box>
      )}

      {tipo === "image" && (
        <Box mb={2}>
          {cell.content.src === LOGO_SENTINEL && (
            <Typography variant="body2" style={{ opacity: 0.75, marginBottom: 8 }}>
              Usando el logo de Dashport por defecto. Sube una foto para reemplazarlo.
            </Typography>
          )}
          <EditableField
            fieldObject={{
              fieldName: "src",
              label: "Imagen",
              type: "photo",
              defaultValue: cell.content.src === LOGO_SENTINEL ? "" : cell.content.src || "",
              route: "printLabels/evento_" + eventoIndex,
            }}
            onSubmit={(values) => onChange({ content: { type: "image", src: values.src } })}
          />
        </Box>
      )}
    </Box>
  );
}
